import { Link, useLocation } from "react-router-dom";
import { KeyRound, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { AdoPatErrorCode } from "@/lib/api/work-items";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { ADO_SETUP_STEP, adoSetupRedirect, isToolboxPatPage, TOOLBOX_SETUP_PATH } from "@/lib/toolbox/page-names";

export interface AdoPatCalloutProps {
  /** `ADO_PAT_INVALID`: ADO rejected the saved PAT (expired or revoked). `ADO_PAT_REQUIRED`: none saved. */
  code: AdoPatErrorCode;
}

/** Inside SpecTree Toolbox: the expired-token words, sending to the same Setup step as the run queue's Fix it for ADO_PAT_INVALID (GROOMING_QUEUE_SETUP_COPY). */
export const TOOLBOX_PAT_EXPIRED_TEXT =
  "Azure DevOps didn't accept your saved token. It has probably expired or been revoked. Connect Azure DevOps again on the Setup page.";
export const TOOLBOX_PAT_REQUIRED_TEXT = "Connect Azure DevOps on the Setup page to see your items.";

/**
 * Where the callout sends someone inside SpecTree Toolbox: Setup's Connect
 * Azure DevOps step, coming back to this page after a pass when it is a
 * Toolbox page Setup knows. Pure; exported for tests.
 */
export function toolboxPatFixPath(pathname: string, search = ""): string {
  if (isToolboxPatPage(pathname)) return adoSetupRedirect(pathname, search);
  return `${TOOLBOX_SETUP_PATH}?${new URLSearchParams({ step: ADO_SETUP_STEP }).toString()}`;
}

/**
 * Shown instead of an error page when Azure DevOps rejects the user's PAT.
 * PATs expire and admins can cap their lifetime, so this is an expected,
 * renewable state. Inside SpecTree Toolbox (epic PI-SPEC-SW-E288) the person
 * renews it on Setup's Connect Azure DevOps step, where the token is tested;
 * in the plain SpecTree web they save it in Settings.
 */
export function AdoPatCallout({ code }: AdoPatCalloutProps) {
  const inToolbox = useToolboxBridge() !== null;
  const location = useLocation();
  const expired = code === "ADO_PAT_INVALID";

  if (inToolbox) {
    return (
      <Card className="border-yellow-500/50 bg-yellow-500/5" role="alert">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-5 w-5 text-yellow-500" />
            {expired ? "Connect Azure DevOps again" : "Connect Azure DevOps"}
          </CardTitle>
          <CardDescription>{expired ? TOOLBOX_PAT_EXPIRED_TEXT : TOOLBOX_PAT_REQUIRED_TEXT}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to={toolboxPatFixPath(location.pathname, location.search)}>
              <KeyRound className="mr-2 h-4 w-4" />
              Open Setup
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-yellow-500/50 bg-yellow-500/5" role="alert">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-5 w-5 text-yellow-500" />
          {expired ? "Renew your Azure DevOps PAT" : "Azure DevOps PAT required"}
        </CardTitle>
        <CardDescription>
          {expired
            ? "Azure DevOps rejected your saved personal access token. It has probably expired or been revoked. Create a new organisation-scoped PAT with Work Items (Read & Write) and save it in Settings."
            : "Save an organisation-scoped Azure DevOps PAT with Work Items (Read & Write) in Settings to see your items."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" />
            Go to Settings
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
